import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './DesktopHeaderCatalogue.styles';
import { useDesktopHeaderCategories } from './DesktopHeaderCatalogue.hooks';
import { DesktopHeaderCategories } from './DesktopHeaderCategories';
import { useDesktopCatalogueStore } from '@/store/useDesktopCatalogueStore';
import { useNavMenuStore } from '@/store/useNavMenuStore';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { Portal } from '@/components/UI/Portal/Portal';

export const DesktopHeaderCatalogue = () => {
  const { t } = useTranslation();

  const [isDesktopCatalogueOpen, toggleDesktopCatalogue] =
    useDesktopCatalogueStore(state => [
      state.isDesktopCatalogueOpen,
      state.toggleDesktopCatalogue,
    ]);
  const [isNavOpen, toggleNav] = useNavMenuStore(state => [
    state.isNavOpen,
    state.toggleNav,
  ]);

  const {
    items,
    loading,
    selectedItemId,
    setSelectedItemId,
    subItems,
    selectedSubItemId,
    setSelectedSubItemId,
    primaryPropertyOptions,
    selectedPrimaryPropertyOptionId,
    setSelectedPrimaryPropertyOptionId,
  } = useDesktopHeaderCategories(isDesktopCatalogueOpen);
  const [isColumnsShown, setIsColumnsShown] = useState<boolean>(true);
  const secondColumnShown = useMemo(() => {
    return isColumnsShown && !!subItems.length;
  }, [isColumnsShown, subItems]);
  const thirdColumnShown = useMemo(() => {
    return isColumnsShown && !!primaryPropertyOptions.length;
  }, [isColumnsShown, primaryPropertyOptions]);

  function onClick() {
    toggleDesktopCatalogue();
    if (isNavOpen) toggleNav();
  }

  function onMouseEnter() {
    setIsColumnsShown(true);
  }

  function onMouseLeave() {
    setIsColumnsShown(false);
  }

  return (
    <>
      <S.DesktopHeaderCatalogueBtn
        data-testid={CommonTestIds.desktopHeaderCatalogue.button}
        $isOpen={isDesktopCatalogueOpen}
        onClick={onClick}
      >
        <S.DesktopHeaderCatalogueBtnIcon $isOpen={isDesktopCatalogueOpen} />
        <span>{t('catalogue.catalogue')}</span>
      </S.DesktopHeaderCatalogueBtn>
      <Portal>
        <S.DesktopHeaderCatalogueOverlay
          data-testid={CommonTestIds.desktopHeaderCatalogue.overlay}
          isOpen={isDesktopCatalogueOpen}
          onClick={toggleDesktopCatalogue}
        />
      </Portal>
      {isDesktopCatalogueOpen && (
        <S.DesktopHeaderCatalogueWrapper>
          <S.DesktopHeaderCatalogueBox>
            <S.DesktopHeaderCatalogueContent
              data-testid={CommonTestIds.desktopHeaderCatalogue.content}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              <DesktopHeaderCategories
                items={items}
                selectedItemId={selectedItemId}
                onSelect={setSelectedItemId}
                isFirstColumn
                loading={loading}
              />
              {secondColumnShown && (
                <DesktopHeaderCategories
                  items={subItems}
                  selectedItemId={selectedSubItemId}
                  onSelect={setSelectedSubItemId}
                />
              )}
              {thirdColumnShown && (
                <DesktopHeaderCategories
                  items={primaryPropertyOptions}
                  selectedItemId={selectedPrimaryPropertyOptionId}
                  onSelect={setSelectedPrimaryPropertyOptionId}
                />
              )}
            </S.DesktopHeaderCatalogueContent>
          </S.DesktopHeaderCatalogueBox>
        </S.DesktopHeaderCatalogueWrapper>
      )}
    </>
  );
};
